import { useId, type ReactNode } from 'react';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import type { InstanceApi } from '@eis/insuranceservice-sdk';
import {
  NOT_STATED,
  formatAdjustmentValue,
  formatAmount,
  formatRate,
  proRataOf,
  taxAmountOf,
  taxNetOf,
  taxTotalOf,
} from '../model/format';
import { notAppliedNoteOf, orderedAdjustments, summaryTaxCodesOf } from '../model/summary';

/**
 * *Aufschlüsselung* (SDD-021 §8.1, §9): the members' totals per tax code, every aggregation
 * adjustment — the ones that did NOT apply too, with why ("Mengenrabatt ab 100 aktiven
 * Mitgliedern (aktuell 1)") — and the totals. Every figure is the summary's; nothing is added up
 * here, not even the adjustments into the total (prompt 29 Decision 9).
 */
export function GroupPremiumSummaryDialog({
  summary,
  open,
  onClose,
  locale,
}: {
  readonly summary: InstanceApi.GroupPremiumSummary | undefined;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly locale: string;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const id = useId();
  const currency = summary?.currency ?? 'EUR';
  const codes = summaryTaxCodesOf(summary);
  const money = (value: string | undefined): string => formatAmount(value, currency, locale);
  const adjustments = orderedAdjustments(summary?.adjustments);

  const totalsTable = (
    totals: InstanceApi.PremiumTotals | undefined,
    titleId: string,
    testId: string,
  ): ReactNode => (
    <Table size="small" aria-labelledby={titleId} data-testid={testId}>
      <TableHead>
        <TableRow>
          <TableCell>{t('premium.dialog.code')}</TableCell>
          <TableCell align="right">{t('premium.dialog.rate')}</TableCell>
          <TableCell align="right">{t('premium.net')}</TableCell>
          <TableCell align="right">{t('premium.taxTotal')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {codes.map((code) => (
          <TableRow key={code.code}>
            <TableCell>{code.code}</TableCell>
            <TableCell align="right">{formatRate(code.rate, locale)}</TableCell>
            <TableCell align="right">{money(taxNetOf(totals?.tax, code.code))}</TableCell>
            <TableCell align="right">{money(taxAmountOf(totals?.tax, code.code))}</TableCell>
          </TableRow>
        ))}
        <TableRow>
          <TableCell sx={{ fontWeight: 600 }} colSpan={2}>
            {t('premium.breakdown.sums')}
          </TableCell>
          <TableCell align="right" sx={{ fontWeight: 600 }}>
            {money(totals?.net)}
          </TableCell>
          <TableCell align="right" sx={{ fontWeight: 600 }}>
            {taxTotalOf(totals) === undefined ? NOT_STATED : money(taxTotalOf(totals))}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell colSpan={3} sx={{ fontWeight: 600 }}>
            {t('premium.gross')}
          </TableCell>
          <TableCell align="right" sx={{ fontWeight: 600 }} data-testid={`${testId}-gross`}>
            {money(totals?.gross)}
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" aria-labelledby={`${id}-title`}>
      <DialogTitle id={`${id}-title`}>{t('premium.dialog.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Stack spacing={1}>
            <Typography variant="h4" component="h3" id={`${id}-members`}>
              {t('premium.dialog.membersTitle')}
            </Typography>
            {totalsTable(summary?.members, `${id}-members`, 'dialog-members')}
          </Stack>

          <Stack spacing={1}>
            <Typography variant="h4" component="h3" id={`${id}-adjustments`}>
              {t('premium.dialog.adjustmentsTitle')}
            </Typography>
            {adjustments.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('premium.dialog.noAdjustments')}
              </Typography>
            ) : (
              <Table
                size="small"
                aria-labelledby={`${id}-adjustments`}
                data-testid="dialog-adjustments"
              >
                <TableHead>
                  <TableRow>
                    <TableCell>{t('premium.dialog.adjustment')}</TableCell>
                    <TableCell>{t('premium.dialog.value')}</TableCell>
                    <TableCell align="right">{t('premium.net')}</TableCell>
                    <TableCell align="right">{t('premium.taxTotal')}</TableCell>
                    <TableCell align="right">{t('premium.gross')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {adjustments.map((adjustment) => {
                    const note = notAppliedNoteOf(adjustment);
                    const applied = note === undefined;
                    const muted = applied ? {} : { color: 'text.secondary' };
                    const proRata = proRataOf(adjustment.proRata);
                    return (
                      <TableRow
                        key={adjustment.key ?? ''}
                        data-testid={applied ? 'adjustment-applied' : 'adjustment-not-applied'}
                      >
                        <TableCell sx={muted}>
                          <Stack spacing={0.25}>
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                              <Typography variant="body2" component="span" sx={muted}>
                                {adjustment.label === undefined || adjustment.label === ''
                                  ? adjustment.key
                                  : adjustment.label}
                              </Typography>
                              <Chip
                                size="small"
                                variant="outlined"
                                color={applied ? 'primary' : 'default'}
                                label={
                                  applied
                                    ? t('premium.dialog.applied')
                                    : t('premium.dialog.notApplied')
                                }
                              />
                            </Stack>
                            {note !== undefined && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                data-testid="not-applied-reason"
                              >
                                {t(`premium.dialog.notAppliedReason.${note.reason}`, {
                                  min: note.min ?? '',
                                  count: note.count ?? '',
                                  code: note.code,
                                })}
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell sx={muted}>
                          <Stack spacing={0.25}>
                            <span>
                              {(
                                ['PERCENT', 'AMOUNT', 'TIERED_PERCENT', 'MINIMUM'] as const
                              ).includes(adjustment.type as never)
                                ? t(`premium.dialog.type.${adjustment.type as 'PERCENT'}`)
                                : (adjustment.type ?? '')}
                            </span>
                            <span>
                              {formatAdjustmentValue(
                                adjustment.type,
                                applied ? adjustment.appliedValue : note.value,
                                currency,
                                locale,
                              )}
                              {proRata !== undefined &&
                                ` · ${t('premium.dialog.proRata', { fraction: proRata })}`}
                            </span>
                          </Stack>
                        </TableCell>
                        <TableCell align="right" sx={muted}>
                          {applied ? money(adjustment.net) : NOT_STATED}
                        </TableCell>
                        <TableCell align="right" sx={muted}>
                          {applied
                            ? (adjustment.tax ?? []).map((tax) => (
                                <Typography
                                  key={tax.code ?? ''}
                                  variant="body2"
                                  component="span"
                                  sx={{ display: 'block' }}
                                >
                                  {`${tax.code ?? ''} ${money(tax.amount)}`}
                                </Typography>
                              ))
                            : NOT_STATED}
                        </TableCell>
                        <TableCell align="right" sx={muted}>
                          {applied ? money(adjustment.gross) : NOT_STATED}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Stack>

          <Stack spacing={1}>
            <Typography variant="h4" component="h3" id={`${id}-total`}>
              {t('premium.dialog.totalTitle')}
            </Typography>
            {totalsTable(summary?.total, `${id}-total`, 'dialog-total')}
          </Stack>

          {summary?.oneTime !== undefined && (
            <Stack spacing={1}>
              <Typography variant="h4" component="h3" id={`${id}-one-time`}>
                {t('premium.dialog.oneTimeTitle')}
              </Typography>
              {totalsTable(summary.oneTime, `${id}-one-time`, 'dialog-one-time')}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('premium.dialog.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
